import { Component, EventEmitter, OnInit, Output, inject } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { ListItem } from '../../../../models/api/core/list/list-item';
import { ClaimRow } from '../../../../models/api/view/claim-view/claim-row';
import { NoteTypeInput } from '../../../../models/api/view/claim/documentation/notes/notes-models';
import { Action } from '../../../../models/core/action';
import {
  NoteItem,
  NotesService,
} from '../../../../services/notes/notes.service';
import { Snackbar } from '../../../../services/snackbar/snackbar.service';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { SelectComponent } from '../../../core/controls/select.component';
import { TextareaComponent } from '../../../core/controls/textarea.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { RequiredFieldsNoteComponent } from '../../../core/labels/required-fields-note.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { LoadingState } from '../../../core/panels/loading-panel.component';

@Component({
  selector: 'app-note-edit-page',
  imports: [
    ButtonComponent,
    SelectComponent,
    TextareaComponent,
    ActionRowComponent,
    ReactiveFormsModule,
    RouterModule,
    PageHeaderComponent,
    RequiredFieldsNoteComponent,
  ],
  template: `
    <app-page-header
      label="Create Note"
      headerSize="h1"
      backRouterLink="../notes"
      backLabel="Notes"
    />

    <form [formGroup]="fg" (ngSubmit)="onSubmit()">
      <app-required-fields-note />
      <div class="tw-flex tw-flex-col lg:tw-flex-row tw-mt-3 lg:tw-mt-0">
        <div class="tw-flex tw-flex-col tw-w-full lg:tw-w-1/3 tw-gap-0">
          @if (showLevel) {
            <app-select
              [options]="noteLevel"
              optionLabel="description"
              optionValue="value"
              class="tw-w-full"
              [virtualScroll]="false"
              [control]="fg.controls.level"
              label="Level"
              [multiple]="false"
              [filter]="false"
              [showClear]="false"
            />
          }
          <app-select
            [options]="noteTypes"
            optionLabel="display"
            optionValue="code"
            class="tw-w-full"
            [virtualScroll]="false"
            [control]="fg.controls.noteType"
            label="Type"
            [multiple]="false"
            [showClear]="false"
          />
        </div>
        <div class="tw-flex tw-flex-col tw-w-full lg:tw-w-2/3 lg:tw-pl-4">
          <app-textarea
            [control]="fg.controls.text"
            label="Text"
            rows="16"
          ></app-textarea>
        </div>
      </div>

      <app-action-row useMobileLayout>
        <app-button
          right1
          type="submit"
          label="Create Note"
          [loading]="loading"
          (click)="setAction('create')"
        />
        <app-button
          right2
          emphasis="Low"
          type="submit"
          label="Save Draft"
          [loading]="loading"
          (click)="setAction('draft')"
        />
        @if (showDeleteDraft) {
          <app-button
            right3
            emphasis="Low"
            label="Delete Draft"
            type="submit"
            (click)="setAction('delete')"
          />
        }
        <app-button
          right4
          routerLink="../notes"
          emphasis="Low"
          label="Cancel"
        />
      </app-action-row>
    </form>
  `,
  styles: ``,
})
export class NoteEditPageComponent extends BaseComponent implements OnInit {
  private snackbar = inject(Snackbar);
  private notesService = inject(NotesService);
  private route = inject(ActivatedRoute);

  @Output()
  search = new EventEmitter();

  @Output()
  clear = new EventEmitter();
  loading = false;
  showLevel = false;
  showDeleteDraft = false;

  protected saveAction: Action[] = [
    { label: 'Note', key: 'note' },
    { label: 'Draft', key: 'draft' },
  ];

  protected noteAction: Action[] = [
    { label: 'None', key: 'none' },
    { label: 'Add examiner diary', key: 'note' },
    { label: 'Add return call request', key: 'call' },
  ];

  protected state: LoadingState = 'Loading';

  fg = new FormGroup({
    level: new FormControl<ListItem[]>(null),
    noteType: new FormControl<ListItem[]>(null, Validators.required),
    text: new FormControl<string>(null, Validators.required),
  });

  noteLevel: any[] = [
    { description: 'Claim', value: 'Claim' },
    { description: 'Claimant', value: 'Claimant' },
    { description: 'Event', value: 'Event' },
  ];

  noteTypes: any[] = [];

  claim: ClaimRow;

  draft: any;

  actionMode: 'create' | 'draft' | 'delete' = 'create';

  private noteItem: NoteItem;

  @HandleLoadingState()
  async ngOnInit() {
    this.claim = this.view.currentClaim();
    this.noteItem = this.notesService.getNoteItem(this.claim);
    this.showLevel = !(
      this.claim.lineOfBusiness === 'LA' || this.claim.lineOfBusiness === 'AD'
    );
    await this.loadNoteTypes(this.claim);
    this.view.hideLoading();
  }

  private createNoteTypeInput(claim: any): NoteTypeInput {
    const input: NoteTypeInput = {
      absenceUID: '',
      clientNumber: claim.contractNumber,
    };
    switch (claim.recordSource) {
      case 'J':
        input.claimUID = claim.recordId;
        break;
      case 'T':
        input.absenceUID = claim.fileNumber;
        break;
      case 'R':
        input.accommodationRequestID = claim.recordId;
        break;
    }
    return input;
  }

  async loadNoteTypes(claim: any) {
    this.noteTypes =
      (
        await this.noteItem.getCreationNoteTypes(
          this.createNoteTypeInput(claim),
        )
      )?.availableTypes?.filter((t) => t.code) ?? [];

    if (this.noteLevel.length > 0) {
      this.fg.controls.level.setValue(this.noteLevel[0].value);
    }

    if (this.noteTypes.length > 0) {
      this.fg.controls.noteType.enable({ emitEvent: false });
      this.fg.controls.noteType.setValue(this.noteTypes[0].code);
      if (this.noteTypes.length === 1) {
        this.fg.controls.noteType.disable({ emitEvent: false });
      }
    } else {
      this.fg.controls.noteType.enable({ emitEvent: false });
      this.fg.controls.noteType.reset(null, { emitEvent: false });
    }

    this.setDraftNote();
  }

  private draftTextFromStorage(text: string | null | undefined): string {
    if (text == null) {
      return '';
    }
    return text.replace(/<br\s*\/?>/gi, '\n');
  }

  private setDraftNote() {
    this.draft = this.noteItem.getSelectedDraft();
    if (this.draft) {
      if (this.draft.level) {
        this.fg.controls.level.setValue(this.draft.level);
      }
      this.noteTypes
        .filter((t) => t.display === this.draft.type)
        .forEach((t) => {
          this.fg.controls.noteType.setValue(t.code);
        });
      this.fg.controls.text.setValue(
        this.draftTextFromStorage(this.draft.text),
      );
      this.showDeleteDraft = true;
    } else {
      this.showDeleteDraft = false;
    }
  }

  setAction(mode: 'create' | 'draft' | 'delete') {
    this.actionMode = mode;
  }

  async onSubmit() {
    if (!this.fg.valid) {
      this.fg.markAllAsTouched();
      return;
    }

    const userName = this.user.lastName + ', ' + this.user.firstName;
    const selectedValue = this.fg.getRawValue().noteType;
    const selectedType = this.noteTypes.find((t) => t.code === selectedValue);
    const typeText =
      userName +
      ' created diary for note type ' +
      (selectedType ? selectedType.display : '');
    const isCreateDiary =
      selectedType.code !== 'ND' &&
      (this.claim.status === 'Open' ||
        this.claim.status === 'Incident' ||
        (this.claim.status === 'Closed' &&
          (this.claim.lineOfBusiness === 'LA' ||
            this.claim.lineOfBusiness === 'AD')));
    const createFinalNote = this.actionMode === 'create';
    const input: any = {
      createNote: {
        text: this.fg.controls.text.value,
        level:
          this.claim.recordSource === 'T'
            ? 'Case'
            : this.fg.controls.level.value,
        type: this.fg.getRawValue().noteType,
        clientNumber: this.claim.contractNumber,
        draftUID: this.draft ? this.draft.draftUID : null,
        claimUID: this.claim.recordId,
        absenceUID: this.claim.fileNumber,
        employeeUID: this.claim.hrMasterUid,
        accommodationRequestID: this.claim.recordId,
        accommodationRequestNumber: this.claim.fileNumber,
        createDiary: createFinalNote && isCreateDiary,
        diaryText:
          createFinalNote && isCreateDiary
            ? typeText
            : (this.fg.controls.text.value ?? ''),
      },
      key: {
        recordSource: this.claim.recordSource,
        recordId: this.claim.recordId,
      },
    };

    if (this.actionMode === 'delete' && this.draft) {
      await this.noteItem.deleteDraft(input);
    } else {
      if (this.actionMode === 'draft') {
        await this.noteItem.upsertDraft(input);
      } else if (this.actionMode === 'create') {
        const result = await this.noteItem.createNote(input);
        if (result && isCreateDiary) {
          this.snackbar.show(
            'Your note has been sent and a diary has been created for the examiner.',
            'Success',
          );
        } else if (result) {
          this.snackbar.show(
            'Your note has been sent. A diary has not been created for the examiner.',
            'Success',
          );
        }
      }
    }
    this.router.navigate(['../notes'], { relativeTo: this.route });
  }
}
